import { environment } from "@app/relay/relay-environment";
import { Link, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, useLazyLoadQuery } from "react-relay";

import type { queueFragment_Query } from "@app/queries/queueFragment_Query.graphql";
import type { queueFragment_CancelMutation } from "@app/queries/queueFragment_CancelMutation.graphql";
import JobCard, { type QueueJob, stateOf } from "./job-card";
import { accountQueueQuery, cancelMutation } from "./queue-fragment";

// Every page carries this, so it must cost nothing when there is nothing to say:
// it renders NULL when the queue is empty, and only polls while something is in
// flight. A queue indicator that keeps hitting the server after everything has
// settled is a bill you pay for leaving a tab open.
const POLL_MS = 2000;

export default function QueueFooter() {
  // Not on /queue. The page below it says everything this does and polls the same
  // query, so showing both duplicates the panel AND doubles the request rate on
  // the one page guaranteed to have something in flight.
  const onQueuePage = useRouterState({
    select: (state) => state.location.pathname === "/queue",
  });
  const [tick, setTick] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const data = useLazyLoadQuery<queueFragment_Query>(
    accountQueueQuery,
    { history: 5 },
    // A fetchKey bump does nothing under store-or-network — the store answers and
    // the poll never reaches the server. network-only is what makes this move.
    {
      fetchKey: tick,
      fetchPolicy: tick > 0 ? "network-only" : "store-or-network",
    }
  );

  const queue = data.accountQueue;
  const active = queue.active as ReadonlyArray<QueueJob>;
  const running = queue.running;
  const polling = queue.activeCount > 0;

  // FETCH FIRST, then bump the key. Bumping blindly makes useLazyLoadQuery do the
  // fetch itself, and a fetch that fails there THROWS on the next render — which,
  // for a component mounted on every page, blanks the whole app over one dropped
  // poll. Fetching here means the key only advances once there is a fresh result
  // in the store, and a failed poll is a no-op that leaves the last good state up.
  const refresh = useCallback(
    () =>
      fetchQuery<queueFragment_Query>(
        environment,
        accountQueueQuery,
        { history: 5 },
        { fetchPolicy: "network-only" }
      )
        .toPromise()
        .then(() => setTick((n) => n + 1))
        .catch(() => {}),
    []
  );

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!polling || onQueuePage) return;
    timer.current = setInterval(() => void refresh(), POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [polling, onQueuePage, refresh]);

  const stop = (jobId: number) => {
    setBusy(true);
    commitMutation<queueFragment_CancelMutation>(environment, {
      mutation: cancelMutation,
      variables: { jobId },
      onCompleted: () => {
        setBusy(false);
        void refresh();
      },
      onError: () => setBusy(false),
    });
  };

  if (onQueuePage || queue.activeCount === 0) return null;

  const headline =
    running?.currentObject ??
    (active[0] ? `${active[0].label} — queued` : "Working…");
  const within =
    running?.rowsTotal && running.rowsTotal > 0
      ? Math.min(
          100,
          Math.round((running.rowsProcessed / running.rowsTotal) * 100)
        )
      : null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-40 flex w-full max-w-md flex-col items-end gap-2">
      {open && (
        <div className="pointer-events-auto max-h-[60vh] w-full overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-2 shadow-lg">
          <div className="mb-2 flex items-center gap-2 px-1">
            <span className="text-xs font-semibold text-gray-700">
              On this account
            </span>
            <Link
              to="/queue"
              onClick={() => setOpen(false)}
              className="ml-auto text-xs text-blue-600 hover:underline"
            >
              See everything
            </Link>
          </div>
          <div className="space-y-2">
            {active.map((job) => (
              <JobCard
                key={job.jobId}
                job={job}
                busy={busy}
                compact
                onStop={stop}
                onRetry={() => undefined}
              />
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="pointer-events-auto flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left shadow-lg hover:border-gray-300"
      >
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-gray-800">
            {headline}
          </span>
          <span className="block text-xs text-gray-500">
            {queue.activeCount} job{queue.activeCount === 1 ? "" : "s"} on this
            account
            {within != null ? ` · ${within}%` : ""}
            {running && stateOf(running as QueueJob) === "waiting"
              ? " · waiting"
              : ""}
          </span>
        </span>
        <span className="shrink-0 text-xs text-gray-400">
          {open ? "Hide" : "Show"}
        </span>
      </button>
    </div>
  );
}
